import type { GtmTrackingContext } from './gtmTrackingContext';
import { getGtmTrackingContext } from './gtmTrackingContext';
import { reportSensitiveDataBlocked } from './sensitiveDataMonitor';

const FORBIDDEN_CARD_KEY_PATTERN = /card|cvv|ccv|cvc|pan|credit|holder|expiry|expiration|security.?code|numero.?cartao|cartao/i;
const CARD_SEQUENCE_PATTERN = /(?:\d[ -]?){13,19}/g;
const SAFE_FIELD_PATH_PATTERN = /(?:^|\.)gtm\.|phone|email|page_path|page_location|page_title|page_referrer|_url$/i;
const REDACTED_VALUE = '[REDACTED]';

export type BlockReason = 'forbidden_key' | 'card_number';

export interface BlockedDataLayerField {
  field: string;
  reason: BlockReason;
}

interface SanitizeResult {
  value: unknown;
  blockedFields: BlockedDataLayerField[];
}

function mergeBlockedFields(
  current: BlockedDataLayerField[],
  next: BlockedDataLayerField[],
): BlockedDataLayerField[] {
  const merged = [...current];

  next.forEach((item) => {
    const alreadyExists = merged.some(
      (existing) => existing.field === item.field && existing.reason === item.reason,
    );
    if (!alreadyExists) {
      merged.push(item);
    }
  });

  return merged;
}

function isSafeFieldPath(fieldPath: string): boolean {
  return SAFE_FIELD_PATH_PATTERN.test(fieldPath);
}

function passesLuhnCheck(digits: string): boolean {
  if (digits.length < 13 || digits.length > 19) {
    return false;
  }

  let sum = 0;
  let shouldDouble = false;

  for (let index = digits.length - 1; index >= 0; index -= 1) {
    let digit = Number.parseInt(digits.charAt(index), 10);
    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }
    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
}

function containsValidCardNumber(value: string): boolean {
  const matches = value.match(CARD_SEQUENCE_PATTERN) || [];

  return matches.some((match) => {
    const digits = match.replace(/\D/g, '');
    return passesLuhnCheck(digits);
  });
}

function sanitizeStringValue(value: string, fieldPath: string): SanitizeResult {
  if (isSafeFieldPath(fieldPath)) {
    return { value, blockedFields: [] };
  }

  if (containsValidCardNumber(value)) {
    return {
      value: REDACTED_VALUE,
      blockedFields: [{ field: fieldPath, reason: 'card_number' }],
    };
  }

  return { value, blockedFields: [] };
}

function sanitizeValue(key: string, value: unknown, fieldPath: string): SanitizeResult {
  if (FORBIDDEN_CARD_KEY_PATTERN.test(key)) {
    return {
      value: REDACTED_VALUE,
      blockedFields: [{ field: fieldPath, reason: 'forbidden_key' }],
    };
  }

  if (typeof value === 'string') {
    return sanitizeStringValue(value, fieldPath);
  }

  if (Array.isArray(value)) {
    return sanitizeUnknown(value, fieldPath);
  }

  if (value !== null && typeof value === 'object') {
    return sanitizeRecord(value as Record<string, unknown>, fieldPath);
  }

  return { value, blockedFields: [] };
}

function sanitizeRecord(record: Record<string, unknown>, parentPath = ''): SanitizeResult {
  const sanitized: Record<string, unknown> = {};
  let blockedFields: BlockedDataLayerField[] = [];

  Object.entries(record).forEach(([key, value]) => {
    const fieldPath = parentPath ? `${parentPath}.${key}` : key;
    const result = sanitizeValue(key, value, fieldPath);
    sanitized[key] = result.value;
    blockedFields = mergeBlockedFields(blockedFields, result.blockedFields);
  });

  return { value: sanitized, blockedFields };
}

export function sanitizeUnknown(value: unknown, parentPath = ''): SanitizeResult {
  if (Array.isArray(value)) {
    let blockedFields: BlockedDataLayerField[] = [];
    const sanitizedArray = value.map((item, index) => {
      const result = sanitizeUnknown(item, `${parentPath}[${index}]`);
      blockedFields = mergeBlockedFields(blockedFields, result.blockedFields);
      return result.value;
    });

    return { value: sanitizedArray, blockedFields };
  }

  if (value !== null && typeof value === 'object') {
    return sanitizeRecord(value as Record<string, unknown>, parentPath);
  }

  if (typeof value === 'string') {
    return sanitizeStringValue(value, parentPath || 'value');
  }

  return { value, blockedFields: [] };
}

function notifyBlockedFields(blockedFields: BlockedDataLayerField[]): void {
  if (blockedFields.length === 0 || typeof window === 'undefined') {
    return;
  }

  const trackingContext: GtmTrackingContext = getGtmTrackingContext();
  const pagePath = window.location.pathname;

  blockedFields.forEach((blockedField) => {
    if (import.meta.env.DEV) {
      console.warn('[GTM Security] Sensitive data blocked:', {
        ...blockedField,
        pagePath,
      });
    }

    void reportSensitiveDataBlocked({
      blockedField: blockedField.field,
      blockReason: blockedField.reason,
      pagePath,
      eventTitle: trackingContext.eventTitle,
      eventSlug: trackingContext.eventSlug,
      gtmContainerId: trackingContext.gtmContainerId,
    });
  });
}

let guardInstalled = false;

export function installDataLayerGuard(): void {
  if (guardInstalled || typeof window === 'undefined') {
    return;
  }

  guardInstalled = true;
  window.dataLayer = window.dataLayer || [];

  const dataLayer = window.dataLayer as unknown as {
    push: (...items: unknown[]) => number;
  };

  const originalPush = dataLayer.push.bind(dataLayer);

  dataLayer.push = (...items: unknown[]) => {
    const sanitizedItems: unknown[] = [];
    let allBlockedFields: BlockedDataLayerField[] = [];

    items.forEach((item) => {
      const result = sanitizeUnknown(item);
      sanitizedItems.push(result.value);
      allBlockedFields = mergeBlockedFields(allBlockedFields, result.blockedFields);
    });

    notifyBlockedFields(allBlockedFields);

    return originalPush(...sanitizedItems);
  };
}
